import { html } from "../../render";
import { attr, cn, numberToPxValue, spreadAttrs, styleVars, } from "../../utils";
import { LpIcon } from "../icon";
import { LpSkeletonLine } from "../skeleton-line";
import { LpTooltip } from "../tooltip";
export const LP_TABLE_ALIGNMENTS = ["start", "center", "end"];
export const LP_TABLE_STICKY_EDGES = ["start", "end"];
export const LP_TABLE_SKELETON_DEFAULT_ROWS = 10;
const rootManagedAttrs = ["data-lp-component", "class", "data-layout"];
const sectionManagedAttrs = ["class"];
const headerManagedAttrs = ["class", "data-sticky"];
const cellManagedAttrs = [
    "data-lp-part",
    "class",
    "scope",
    "data-align",
    "data-lp-table-column",
    "data-sticky",
    "style",
    "hidden",
];
const stateBodyManagedAttrs = ["class"];
const skeletonBodyManagedAttrs = ["class", "aria-busy"];
function getLpTableColumnStyle(column) {
    return styleVars({
        "--lp-table-column-size": numberToPxValue(column.width),
        "--lp-table-column-min-size": numberToPxValue(column.minWidth),
    });
}
function renderSortIcon() {
    return html `
        <span class="lp-table_sort-icons" aria-hidden="true">
            ${LpIcon({ name: "caret-up-down", className: "lp-table_sort-icon lp-table_sort-icon-neutral" })}
            ${LpIcon({ name: "caret-up", className: "lp-table_sort-icon lp-table_sort-icon-active" })}
        </span>
    `;
}
function LpTableRoot({ caption, children, columns, layout = columns ? "fixed" : "auto", className, attributes, }) {
    return html `
        <div
            data-lp-component="Table"
            class="${cn("lp-table", className, attributes?.class)}"
            data-layout="${layout}"
            ${spreadAttrs(attributes, { omit: rootManagedAttrs })}
        >
            <div class="lp-table_scroll" data-lp-part="scroll">
                <table class="lp-table_table">
                    <caption class="lp-table_caption">${caption}</caption>
                    ${columns
        ? html `
                            <colgroup>
                                ${columns.map((column) => html `
                                    <col
                                        class="lp-table_col"
                                        data-lp-table-column="${column.key}"
                                        ${attr("style", getLpTableColumnStyle(column))}
                                        ${attr("hidden", column.visibleByDefault === false)}
                                    >
                                `)}
                            </colgroup>
                        `
        : ""}
                    ${children}
                </table>
            </div>
        </div>
    `;
}
function LpTableHeader({ children, sticky = false, className, attributes, }) {
    return html `
        <thead
            class="${cn("lp-table_head", className, attributes?.class)}"
            ${attr("data-sticky", sticky)}
            ${spreadAttrs(attributes, { omit: headerManagedAttrs })}
        >${children}</thead>
    `;
}
function LpTableBody({ children, className, attributes, }) {
    return html `
        <tbody
            class="${cn("lp-table_body", className, attributes?.class)}"
            ${spreadAttrs(attributes, { omit: sectionManagedAttrs })}
        >${children}</tbody>
    `;
}
function LpTableFooter({ children, className, attributes, }) {
    return html `
        <tfoot
            class="${cn("lp-table_foot", className, attributes?.class)}"
            ${spreadAttrs(attributes, { omit: sectionManagedAttrs })}
        >${children}</tfoot>
    `;
}
function LpTableRow({ children, className, attributes, }) {
    return html `
        <tr
            class="${cn("lp-table_row", className, attributes?.class)}"
            ${spreadAttrs(attributes, { omit: sectionManagedAttrs })}
        >${children}</tr>
    `;
}
function LpTableHead({ column, className, attributes, }) {
    const tooltipTitle = column.tooltipTitle ?? column.label;
    const tooltipLabel = column.tooltipLabel
        ?? (typeof tooltipTitle === "string" || typeof tooltipTitle === "number"
            ? String(tooltipTitle)
            : column.key);
    const mergedAttributes = { ...column.headerAttributes, ...attributes };
    const style = [
        getLpTableColumnStyle(column),
        typeof mergedAttributes.style === "string" ? mergedAttributes.style : undefined,
    ].filter(Boolean).join(" ");
    return html `
        <th
            data-lp-part="head"
            class="${cn("lp-table_header-cell", className, mergedAttributes.class)}"
            scope="col"
            data-align="${column.align ?? "start"}"
            data-lp-table-column="${column.key}"
            ${attr("data-sticky", column.sticky)}
            ${attr("style", style)}
            ${attr("hidden", column.visibleByDefault === false)}
            ${spreadAttrs(mergedAttributes, { omit: cellManagedAttrs })}
        >
            <div class="lp-table_header-content">
                ${column.sortable
        ? html `
                        <button
                            class="lp-table_sort"
                            type="button"
                            aria-label="Sort by ${column.label}"
                            data-direction="none"
                            ${spreadAttrs(column.sortAttributes)}
                        >
                            <span class="lp-table_header-label">${column.label}</span>
                            ${renderSortIcon()}
                        </button>
                    `
        : html `<span class="lp-table_header-label">${column.label}</span>`}
                ${column.description
        ? LpTooltip({
            label: `About ${tooltipLabel}`,
            content: column.description,
        })
        : ""}
            </div>
            ${column.resizable ? LpTableResizeHandle({ column }) : ""}
        </th>
    `;
}
function renderTableCell({ column, children, className, attributes, }, footer) {
    const style = [
        getLpTableColumnStyle(column),
        typeof attributes?.style === "string" ? attributes.style : undefined,
    ].filter(Boolean).join(" ");
    return html `
        <td
            class="${cn("lp-table_cell", footer && "lp-table_footer-cell", className, attributes?.class)}"
            data-align="${column.align ?? "start"}"
            data-lp-table-column="${column.key}"
            ${attr("data-sticky", column.sticky)}
            ${attr("style", style)}
            ${attr("hidden", column.visibleByDefault === false)}
            ${spreadAttrs(attributes, { omit: cellManagedAttrs })}
        >${children}</td>
    `;
}
function LpTableCell(props) {
    return renderTableCell(props, false);
}
function LpTableFootCell(props) {
    return renderTableCell(props, true);
}
function LpTableResizeHandle({ column }) {
    return html `
        <button
            data-lp-part="resize-handle"
            class="lp-table_resize"
            type="button"
            aria-label="Resize ${column.label} column"
            data-lp-table-resize="${column.key}"
            ${attr("data-lp-table-min-width", numberToPxValue(column.minWidth ?? column.width))}
        ><span class="lp-table_resize-line"></span></button>
    `;
}
function LpTableStateBody({ colSpan, children, className, attributes, }) {
    return html `
        <tbody
            class="${cn("lp-table_body", "lp-table_state-body", className, attributes?.class)}"
            ${spreadAttrs(attributes, { omit: stateBodyManagedAttrs })}
        >
            <tr class="lp-table_state-row">
                <td class="lp-table_state-cell" colspan="${colSpan}">${children}</td>
            </tr>
        </tbody>
    `;
}
function renderSkeletonCell(column, rowIndex, columnIndex, label) {
    const { lines = 1, width = 75, secondaryWidth = 48, } = column.skeleton ?? {};
    return LpTableCell({
        column,
        className: "lp-table_skeleton-cell",
        attributes: column.skeletonAttributes,
        children: html `
            ${rowIndex === 0 && columnIndex === 0
            ? html `<span class="lp-table_skeleton-status" role="status">${label}</span>`
            : ""}
            <span class="lp-table_skeleton-lines" aria-hidden="true">
                ${LpSkeletonLine({ width })}
                ${lines === 2 ? LpSkeletonLine({ width: secondaryWidth }) : ""}
            </span>
        `,
    });
}
function LpTableSkeletonBody({ columns, rows = LP_TABLE_SKELETON_DEFAULT_ROWS, label = "Loading table", attributes, }) {
    const rowCount = Math.max(1, Math.floor(rows));
    return html `
        <tbody
            class="${cn("lp-table_body", "lp-table_skeleton-body", attributes?.class)}"
            aria-busy="true"
            ${spreadAttrs(attributes, { omit: skeletonBodyManagedAttrs })}
        >
            ${Array.from({ length: rowCount }, (_, rowIndex) => html `
                <tr class="lp-table_skeleton-row">
                    ${columns.map((column, columnIndex) => renderSkeletonCell(column, rowIndex, columnIndex, label))}
                </tr>
            `)}
        </tbody>
    `;
}
export const LpTable = {
    Root: LpTableRoot,
    Header: LpTableHeader,
    Body: LpTableBody,
    Footer: LpTableFooter,
    Row: LpTableRow,
    Head: LpTableHead,
    Cell: LpTableCell,
    FootCell: LpTableFootCell,
    ResizeHandle: LpTableResizeHandle,
    StateBody: LpTableStateBody,
    SkeletonBody: LpTableSkeletonBody,
};
